import { ref, computed } from 'vue';
import promotionService from '@/services/promotionService';
import type { 
  PromotionCalculationRequest, 
  PromotionCalculationResponse,
  Promotion 
} from '@/types/promotion';
import type { OrderItem } from '@/services/orderService';

export function usePromotions() {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastCalculation = ref<PromotionCalculationResponse | null>(null);
  const activePromotions = ref<Promotion[]>([]);

  /**
   * Calcular promociones para los items de una orden
   */
  const calculatePromotions = async (request: PromotionCalculationRequest): Promise<PromotionCalculationResponse> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await promotionService.calculatePromotions(request);
      lastCalculation.value = result;
      
      return result;
    } catch (err: any) {
      error.value = err.message || 'Error calculating promotions';
      
      // Return fallback result on error
      const fallbackResult: PromotionCalculationResponse = {
        applied_promotions: [],
        total_discount: 0,
        final_total: request.order_total,
        eligible_promotions: [],
        ineligible_promotions: []
      };
      
      lastCalculation.value = fallbackResult;
      return fallbackResult;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Calcular promociones para items de orden (formato existente)
   */
  const calculatePromotionsForOrder = async (
    items: OrderItem[],
    orderDate: string = new Date().toISOString(),
    orderTotal: number,
    customerId?: number
  ): Promise<PromotionCalculationResponse> => {
    const request: PromotionCalculationRequest = {
      items: items.map(item => ({
        menu_item_id: item.menu_item_id,
        variant_id: item.variant_id || undefined,
        quantity: item.quantity,
        unit_price: item.unit_price
      })),
      order_date: orderDate,
      order_total: orderTotal,
      customer_id: customerId
    };

    return await calculatePromotions(request);
  };

  /**
   * Cargar promociones activas
   */
  const loadActivePromotions = async () => {
    try {
      activePromotions.value = await promotionService.getActivePromotions();
    } catch (err: any) {
      console.error('Error loading active promotions:', err);
      activePromotions.value = [];
    }
  };

  /**
   * Verificar si una promoción está activa hoy
   */
  const isPromotionActiveToday = (promotion: Promotion): boolean => {
    if (!promotion.is_active || !promotion.time_condition) {
      return promotion.is_active;
    }

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as any;
    const currentTime = now.toTimeString().slice(0, 5); // HH:mm

    // Verificar día de la semana
    if (promotion.time_condition.days_of_week.length > 0) {
      if (!promotion.time_condition.days_of_week.includes(currentDay)) {
        return false;
      }
    }

    // Verificar hora
    if (promotion.time_condition.start_time && promotion.time_condition.end_time) {
      if (currentTime < promotion.time_condition.start_time || currentTime > promotion.time_condition.end_time) {
        return false;
      }
    }

    // Verificar fecha
    if (promotion.time_condition.start_date || promotion.time_condition.end_date) {
      const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD
      if (promotion.time_condition.start_date && currentDate < promotion.time_condition.start_date) {
        return false;
      }
      if (promotion.time_condition.end_date && currentDate > promotion.time_condition.end_date) {
        return false;
      }
    }

    return true;
  };

  /**
   * Obtener promociones aplicables hoy
   */
  const getTodayPromotions = computed(() => {
    return activePromotions.value.filter(promotion => isPromotionActiveToday(promotion));
  });

  /**
   * Obtener descuento total de la última cálculo
   */
  const getTotalDiscount = computed(() => {
    return lastCalculation.value?.total_discount || 0;
  });

  /**
   * Obtener promociones aplicadas
   */
  const getAppliedPromotions = computed(() => {
    return lastCalculation.value?.applied_promotions || [];
  });

  /**
   * Obtener mensajes de promociones aplicadas
   */
  const getPromotionMessages = computed(() => {
    return lastCalculation.value?.applied_promotions.map(p => p.message) || [];
  });

  /**
   * Resetear estado
   */
  const reset = () => {
    loading.value = false;
    error.value = null;
    lastCalculation.value = null;
  };

  return {
    loading,
    error,
    lastCalculation,
    activePromotions,
    calculatePromotions,
    calculatePromotionsForOrder,
    loadActivePromotions,
    isPromotionActiveToday,
    getTodayPromotions,
    getTotalDiscount,
    getAppliedPromotions,
    getPromotionMessages,
    reset
  };
}

export default usePromotions;
